/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { inject as service } from '@ember/service';
import { restartableTask } from 'ember-concurrency';
import { pct } from 'cio/helpers/pct';
import { shortenNumber } from 'cio/helpers/shorten-number';
import { Measure, Periods, Entities } from 'cio/utils/metrics/common';
import channelInfo from 'cio/utils/newsletter-channel-info';
import { capitalize } from '@ember/string';

import { Metric } from 'cio/utils/metrics/models/metric';
import NewsletterModel from 'cio/models/newsletter';
import ScheduleModel from 'cio/models/schedule';
import ApiService from 'cio/services/api';
import MetricsService from 'cio/services/metrics';
import EnvironmentService from 'cio/services/environment';

interface MetricCell {
  metric: Measure;
  label: string;
  labelTooltipContent: string;
  value: string | number;
  valueClass?: string;
  valueTooltipContent?: string | number;
}

interface CioNewslettersListItemArgs {
  newsletter: NewsletterModel;
  queueTask: any;
  afterDeleteNewsletterAction: () => unknown;
  afterCopyNewsletterAction?: () => unknown;
}

export default class CioNewslettersListItem extends Component<CioNewslettersListItemArgs> {
  @service api!: ApiService;
  @service('metrics') metricsService!: MetricsService;
  @service environment!: EnvironmentService;

  @tracked metric!: Metric;
  @tracked schedule!: ScheduleModel;

  get channelInfo() {
    return channelInfo(this.args.newsletter.channel);
  }

  get metricCells(): MetricCell[] {
    let { totalSent, totalDelivered } = this.args.newsletter;
    let totalFailed = this.metric?.failed?.allTimeTotal ?? 0;

    let deliveredRate = totalSent === 0 ? null : totalDelivered / totalSent;
    let deliveredTooltip = `${totalDelivered} (${pct([deliveredRate])}) delivered out of ${totalSent} sent`;

    let denominator = this.environment.current.metricDenominator;

    return [
      {
        metric: 'failed',
        label: 'Failed',
        labelTooltipContent: `Total failed messages`,
        value: shortenNumber([totalFailed]),
        valueClass: totalFailed > 0 ? 'color-danger' : undefined,
        valueTooltipContent: totalFailed,
      },
      {
        metric: denominator,
        label: capitalize(denominator),
        labelTooltipContent: `Total messages ${denominator}`,
        value: denominator === 'sent' ? totalSent : totalDelivered,
        valueTooltipContent: denominator === 'sent' ? totalSent : deliveredTooltip,
      },
      {
        metric: 'opened',
        label: 'Opened',
        labelTooltipContent: 'Average open rate',
        value: pct([this.metric?.opened?.allTimeRate]),
        valueClass: 'color-green',
      },
      {
        metric: 'clicked',
        label: 'Clicked',
        labelTooltipContent: 'Average click rate',
        value: pct([this.metric?.clicked?.allTimeRate]),
        valueClass: 'color-green',
      },
      {
        metric: 'converted',
        label: 'Converted',
        labelTooltipContent: 'Average conversion rate',
        value: pct([this.metric?.converted?.allTimeRate]),
        valueClass: 'color-green',
      },
    ];
  }
  get visibleMetricCells() {
    return this.metricCells.filter((cell) => this.channelInfo.metrics.includes(cell.metric));
  }

  metricTask = restartableTask(async () => {
    this.metric = await this.metricsService.fetchMetric(
      Entities.NEWSLETTER,
      this.args.newsletter.id,
      Periods.DAYS,
      null,
      this.args.newsletter.channel,
      {
        environmentId: this.environment.current.id,
      },
    );
  });

  scheduleTask = restartableTask(async () => {
    this.schedule = await this.args.newsletter.get('schedule');
  });

  constructor(owner: unknown, args: CioNewslettersListItemArgs) {
    super(owner, args);

    let { channel } = args.newsletter;

    if (channel && (channel as any) !== 'unset') {
      args.queueTask.perform(this.metricTask);
    }

    args.queueTask.perform(this.scheduleTask);
  }
}
